import { useNavigate, useLocation } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { useRealSchoolSettings } from "@/hooks/useRealSchoolSettings";
import { useAuth } from "@/hooks/auth";
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Sparkles, User, LogOut, ShieldCheck, Menu } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router-dom";
import { useSiteLogo } from "@/hooks/useSiteLogo";
import { IconDashboard } from "@tabler/icons-react";

const Header = () => {
  const { settings } = useRealSchoolSettings();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const { signOut, user, userProfile } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { siteLogo } = useSiteLogo();

  // استخدام الشعار العام للموقع مع شعار افتراضي
  const logoUrl =
    siteLogo.url || "/lovable-uploads/ceeee985-6f40-459e-8179-d315fbab21ab.png";
  const logoAlt = siteLogo.alt_text || "شعار الموقع";

  const handleLogout = async () => {
    try {
      await signOut();
      // window.location.href = "/auth";
      navigate("/auth");
    } catch (error) {
      console.error("Error during logout:", error);
      toast({
        title: "خطأ في تسجيل الخروج",
        description: "حدث خطأ أثناء تسجيل الخروج",
        variant: "destructive",
      });
    }
  };

  const handleReturnToAdminDashboard = () => {
    // إزالة بيانات الوصول الإداري والعودة للوحة التحكم
    localStorage.removeItem("isAdminAccess");
    localStorage.removeItem("adminAccessSchool");

    toast({
      title: "تم العودة للوحة التحكم",
      description: "أنت الآن في وضع المدير العام",
    });

    navigate("/admin");
  };

  // Don't show header for admin dashboard or on auth pages
  if (
    location.pathname === "/auth" ||
    location.pathname === "/login" ||
    location.pathname === "/admin-login"
  ) {
    return null;
  }

  // Show header immediately - don't wait for loading to complete
  // استخدام بيانات المدرسة الحالية (من قاعدة البيانات أو من الوصول الإداري أو من المستخدم)
  const getDisplaySchoolName = () => {
    // للمدارس العادية، استخدم البيانات المحدثة من قاعدة البيانات أولاً
    if (settings?.school_name) {
      return settings.school_name;
    }
    if (user?.user_metadata?.school_name) {
      return user.user_metadata.school_name;
    }
    const defaultName = user?.email?.split("@")[0] || "مدرستي";

    return defaultName;
  };

  // دالة لمسح الكاش وإعادة التحميل (للتشخيص)
  const clearCacheAndReload = () => {
    localStorage.removeItem("adminAccessSchool");
    localStorage.removeItem("isAdminAccess");
    window.location.reload();
  };

  const displaySchoolName = getDisplaySchoolName();

  return (
    <header
      className="sticky top-0 z-50 border-b border-white/10 backdrop-blur-xl bg-gradient-to-r from-school-navy/95 via-school-blue/90 to-school-teal/85 shadow-lg"
      dir="rtl"
    >
      {/* Subtle decorative overlay */}

      <div className="container mx-auto px-4 sm:px-6 relative z-10">
        <div className="flex justify-between items-center py-3 sm:py-4">
          {/* Logo and School Name */}
          <div className="flex items-center gap-3 sm:gap-4 flex-shrink-0">
            <div className="relative group">
              {/* مستطيل الشعار مع الزوايا المنحنية */}
              <div className="w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 flex items-center justify-center rounded-tr-2xl rounded-bl-2xl overflow-hidden bg-white backdrop-blur-sm shadow-lg transition-all duration-300 hover:scale-110 hover:shadow-2xl cursor-pointer group">
                <img
                  src={logoUrl}
                  alt={logoAlt}
                  className="w-full h-full object-contain transition-transform duration-300 group-hover:rotate-3"
                />
              </div>
            </div>
            <div className="min-w-0 flex-1">
              {/* Enhanced text styling */}
              <div className="flex flex-col">
                <h1 className="text-lg sm:text-xl lg:text-2xl font-bold text-white mb-1 tracking-wide truncate leading-tight bg-gradient-to-r from-white via-blue-100 to-white bg-clip-text">
                  {displaySchoolName}
                </h1>
                <p className="text-xs sm:text-sm text-blue-100/90 font-medium hidden sm:block leading-relaxed">
                  <span className="flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 bg-blue-200 rounded-full animate-pulse"></span>
                    منصة حاضرون التعليمية
                  </span>
                </p>
                {userProfile?.role === "admin" && (
                  <Badge className="bg-gradient-to-r from-yellow-400/90 to-orange-400/90 text-yellow-900 text-xs mt-2 flex items-center gap-1.5 w-fit px-2 py-1 rounded-lg shadow-lg border border-yellow-300/50">
                    <ShieldCheck className="w-3.5 h-3.5" />
                    <span className="hidden sm:inline font-semibold">
                      وضع المدير
                    </span>
                    <span className="sm:hidden font-semibold">مدير</span>
                  </Badge>
                )}
              </div>
            </div>
          </div>

          {/* Desktop Actions */}
          <div className="hidden md:flex items-center gap-2 lg:gap-3">
            {/* <Link to="/profile">
              <Button
                variant="outline"
                size="sm"
                className="flex items-center gap-1.5 lg:gap-2 border-white/30 bg-white/10 text-white hover:bg-white/20 transition-all duration-300 backdrop-blur-sm text-xs px-3 lg:px-4 py-2 rounded-xl shadow-lg hover:shadow-xl"
              >
                <User className="h-3 w-3 lg:h-4 lg:w-4" />
                <span className="hidden lg:inline">حسابي</span>
              </Button>
            </Link> */}
            <Link to={location.pathname === "/profile" ? ".." : "/profile"}>
              <Button
                variant="outline"
                size="sm"
                className="flex items-center gap-2 border-white/30 bg-white/10 text-white hover:bg-white/20 transition-all duration-300 backdrop-blur-sm px-4 py-2 rounded-xl shadow-lg hover:shadow-xl"
              >
                {location.pathname === "/profile" ? (
                  <IconDashboard className="h-4 w-4" />
                ) : (
                  <User className="h-4 w-4" />
                )}
                {location.pathname === "/profile"
                  ? "العودة للرئيسية"
                  : "الملف الشخصي"}
              </Button>
            </Link>
            {/* {userProfile?.role === "admin" ? (
              <Button
                onClick={handleReturnToAdminDashboard}
                variant="outline"
                size="sm"
                className="flex items-center gap-1.5 lg:gap-2 border-yellow-300/50 bg-yellow-500/10 text-yellow-200 hover:bg-yellow-500/20 transition-all duration-300 backdrop-blur-sm text-xs px-3 lg:px-4 py-2 rounded-xl shadow-lg hover:shadow-xl"
              >
                <ShieldCheck className="h-3 w-3 lg:h-4 lg:w-4" />
                <span className="hidden lg:inline">العودة للوحة التحكم</span>
                <span className="lg:hidden">وحة التحكم</span>
              </Button>
            ) : null} */}

            <Button
              onClick={handleLogout}
              variant="outline"
              size="sm"
              className="flex items-center gap-1.5 lg:gap-2 border-white/30 bg-white/10 text-white hover:bg-white/20 transition-all duration-300 backdrop-blur-sm text-xs px-3 lg:px-4 py-2 rounded-xl shadow-lg hover:shadow-xl"
            >
              <LogOut className="h-3 w-3 lg:h-4 lg:w-4" />
              <span className="hidden lg:inline">خروج</span>
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <Button
            variant="outline"
            size="sm"
            className="md:hidden border-white/30 hover:bg-white/10 backdrop-blur-sm p-2"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            <Menu className="h-4 w-4 text-school-navy stroke-2" />
          </Button>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <div className="md:hidden border-t border-white/20 py-4">
            <div className="flex flex-col gap-2">
              <Link to="/profile" onClick={() => setIsMobileMenuOpen(false)}>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full justify-start gap-2 border-white/30 bg-white/10 text-white hover:bg-white/20 backdrop-blur-sm text-xs"
                >
                  <User className="h-4 w-4" />
                  حسابي
                </Button>
              </Link>

              {userProfile?.role === "admin" ? (
                <Button
                  onClick={() => {
                    setIsMobileMenuOpen(false);
                    handleReturnToAdminDashboard();
                  }}
                  variant="outline"
                  size="sm"
                  className="w-full justify-start gap-2 border-yellow-300/50 bg-yellow-500/10 text-yellow-200 hover:bg-yellow-500/20 backdrop-blur-sm text-xs"
                >
                  <ShieldCheck className="h-4 w-4" />
                  العودة للوحة التحكم
                </Button>
              ) : null}

              <Button
                onClick={() => {
                  setIsMobileMenuOpen(false);
                  handleLogout();
                }}
                variant="outline"
                size="sm"
                className="w-full justify-start gap-2 border-white/30 bg-white/10 text-white hover:bg-white/20 backdrop-blur-sm text-xs"
              >
                <LogOut className="h-4 w-4" />
                تسجيل الخروج
              </Button>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
